import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { SendIcon } from "../icons/Icons";
import { isoToUkDate } from "../conversation/validation";
import { PRIVACY_POLICY_URL } from "../config";
import type { Option } from "../api/types";
import { useI18n } from "../i18n";

type InputBarProps = {
  disabled?: boolean;
  /** Option-only questions: no free text is possible, so the composer pill
   *  hides entirely instead of sitting disabled — it confused testers into
   *  trying to type, and it costs a full row of a phone screen (PROJ-119,
   *  Priya). Chips, errors and the footnote still render. */
  composerHidden?: boolean;
  placeholder?: string;
  /** "date" renders a date picker (DOB); value converts to DD/MM/YYYY. */
  mode?: "text" | "date";
  error?: string | null;
  onSend: (text: string) => void;
  /** Quick-send chips shown above the pill (free-text questions w/ suggestions). */
  chips?: Option[];
  onChip?: (message: string, displayText: string) => void;
  /** Auto-focus the field when it becomes enabled (after the resident has started). */
  focusOnEnable?: boolean;
  /** Start over. Rendered as a quiet footer link; omit to hide it. */
  onReset?: () => void;
  /** Open the "report a problem" form. */
  onReportProblem?: () => void;
};

const today = () => new Date().toISOString().split("T")[0];
const optionValue = (o: Option): string =>
  o.value !== undefined && o.value !== null ? String(o.value) : o.text;

/**
 * Bottom floating input — pixel-matched to index_conversational.html
 * (input-pill-frame + circular send-pill-btn). Controlled, Enter-to-send,
 * auto-growing textarea, double-submit guard, optional date-picker mode.
 */
/** Temporarily hidden at Priya's request (12 Aug 2026). Flip to true to
 *  restore — the reset flow itself is untouched. */
const SHOW_RESET = false;

export function InputBar({
  disabled = false,
  composerHidden = false,
  placeholder,
  mode = "text",
  error = null,
  onSend,
  chips,
  onChip,
  focusOnEnable = false,
  onReset,
  onReportProblem,
}: InputBarProps) {
  const { t } = useI18n();
  const [value, setValue] = useState("");
  const taRef = useRef<HTMLTextAreaElement>(null);

  // Keep the latest message in view when the on-screen keyboard opens. The
  // keyboard shrinks the visual viewport; without this the last question can
  // scroll out of view. Scroll the conversation feed to the bottom.
  const scrollFeedToEnd = () => {
    const feed = document.querySelector<HTMLElement>(".chat-feed");
    if (feed && typeof feed.scrollTo === "function") feed.scrollTo({ top: feed.scrollHeight, behavior: "smooth" });
  };

  const onInputFocus = () => {
    // Fire across the keyboard's open animation (timing varies by device).
    setTimeout(scrollFeedToEnd, 100);
    setTimeout(scrollFeedToEnd, 350);
  };

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const onResize = () => {
      if (document.activeElement === taRef.current) scrollFeedToEnd();
    };
    vv.addEventListener("resize", onResize);
    return () => vv.removeEventListener("resize", onResize);
  }, []);

  // Auto-focus the textarea when a new question enables it, so the resident can
  // type straight away (focus is otherwise lost when the bar disables during
  // "sending"). Only after they've started — keeps the logo strip in view on load.
  useEffect(() => {
    if (focusOnEnable && !disabled && mode === "text") taRef.current?.focus();
  }, [focusOnEnable, disabled, mode]);

  const grow = () => {
    const el = taRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
    }
  };

  const submit = () => {
    const raw = value.trim();
    if (!raw || disabled) return;
    const text = mode === "date" ? isoToUkDate(raw) : raw;
    setValue("");
    if (taRef.current) taRef.current.style.height = "auto";
    onSend(text);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="bottom-input-bar-container" role="region" aria-label="Your reply">
      <div className="input-bar-inner-width">
        {chips && chips.length > 0 && (
          <div className="chips-tray-container">
            {chips.map((o) => (
              <button
                key={o.text}
                type="button"
                className="prompt-chip"
                disabled={disabled}
                onClick={() => onChip?.(optionValue(o), o.text)}
              >
                {o.text}
              </button>
            ))}
          </div>
        )}

        {error && (
          <p className="input-error-text" role="alert">
            {error}
          </p>
        )}

        {!composerHidden && (
        <div className="input-pill-frame">
          {mode === "date" ? (
            <input
              className="input-pill-datepicker"
              type="date"
              max={today()}
              aria-label={t("input.dob")}
              disabled={disabled}
              value={value}
              onFocus={onInputFocus}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submit();
                }
              }}
            />
          ) : (
            <textarea
              ref={taRef}
              className="input-pill-textarea"
              rows={1}
              placeholder={placeholder ?? t("input.typeMessage")}
              aria-label={t("input.typeMessage")}
              disabled={disabled}
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                grow();
              }}
              onFocus={onInputFocus}
              onKeyDown={onKeyDown}
            />
          )}
          <div className="input-right-tools">
            <button
              type="button"
              className="send-pill-btn"
              aria-label={t("input.sendMessage")}
              disabled={disabled || value.trim().length === 0}
              onClick={submit}
            >
              <SendIcon />
            </button>
          </div>
        </div>
        )}

        <p className="bottom-footnote-disclaimer">
          {t("input.confidential")}{" "}
          <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer">
            {t("input.privacyPolicy")}
          </a>
          {/* Reporting the page itself being broken. On the same line as the
              confidentiality sentence (Naveen, 14 Sept), separated by a middot
              so the two read as distinct items rather than one run-on. Wraps to
              a second line on narrow screens. */}
          {onReportProblem && (
            <>
              {" · "}
              <span className="bottom-footnote-support">
                {t("input.techIssue")}{" "}
                {/* A <button>: it opens a dialog, it does not navigate. Styled
                    to sit as a peer of the Privacy Policy link beside it. */}
                <button
                  type="button"
                  className="bottom-footnote-support__link"
                  onClick={onReportProblem}
                >
                  {t("input.reportProblem")}
                </button>
              </span>
            </>
          )}
          {/* Reset lives here rather than the header (hazard log, Priya
              24 Jul): starting over is a rare, destructive action and should
              not sit beside the two things a resident needs mid-conversation.
              A <button>, styled to match the adjacent link — it acts on the
              session, it does not navigate. */}
          {/* Temporarily hidden at Priya's request (12 Aug 2026). The reset
              flow itself is untouched — onReset, the confirmation modal and
              the cache-clearing all remain — so restoring this is uncommenting
              the block below. */}
          {SHOW_RESET && onReset && (
            <>
              {" · "}
              <button type="button" className="bottom-footnote-reset" onClick={onReset}>
                {t("input.startNew")}
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  );
}
